import TableCellContainer from '@libComponents/table/TableCellContainer';
import TableContainer from '@libComponents/table/TableContainer';
import TableHeaderContainer from '@libComponents/table/TableHeaderContainer';
import { Box, TableBody, TableRow, Typography, styled } from '@mui/material';

import { convertToHoursAndMinuteFromSecond, formatTime } from "@libs/dateTime";
import { useAttendanceState } from "../useAttendanceProvider";
import { theme } from 'app/Theme';


const TABLE_DATA = {
    'name': 'Name',
    'designation': 'Designation',
    'branch': 'Branch',
    'inTime': "In Time",
    'outTime': 'Out Time',
    'duration': 'Total Hours',
    'status': 'Status'
};

const LEAVE_STATUS = {
    'in': {
        label: 'Present',
        color: 'db_overview_2'
    },
    'absent': {
        label: 'Absent',
        color: 'db_overview_3'
    },
    'arrivedLate': {
        label: 'Late',
        color: 'db_overview_4'
    },
}

const StyledLabel = styled(Typography)(
    ({ theme: { spacing, palette, typography, shape } }) => ({
        margin: 0,
        background: palette.background.secondary,
        maxWidth: spacing(12),
        textAlign: 'center',
        padding: spacing(.5, 2),
        borderRadius: shape.borderRadius,
    })
);

const StyledBox = styled(Box)(
    ({ theme: { palette, typography, shape } }) => ({
        width: '100%',
        overflow: 'auto',
        minHeight:300,
        '& table': {
            tableLayout: 'fixed',

            '@media screen and (max-width: 768px)': {
                '& thead': {
                    display: 'none'
                },

                '& tbody tr': {
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    padding: '16px',
                    borderTop: `1px solid ${palette.divider}`,
                    borderBottom: `1px solid ${palette.divider}`,
                    marginBottom: 12,
                    backgroundColor: palette.background.paper,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',

                    '& td': {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 0',
                        border: 'none',
                        borderBottom: `1px solid ${palette.action.hover}`,

                        '&:last-child': {
                            borderBottom: 'none',
                            paddingBottom: 0
                        },

                        '&:first-child': {
                            fontSize: '1rem',
                            fontWeight: 600,
                            paddingBottom: 10,
                            marginBottom: 2,
                            borderBottom: `2px solid ${palette.divider}`,
                            color: palette.text.primary,

                            '&:before': {
                                display: 'none'
                            }
                        },

                        '&:before': {
                            content: 'attr(data-heading)',
                            fontWeight: 500,
                            fontSize: '0.85rem',
                            color: palette.text.secondary,
                            textTransform: 'capitalize',
                            flex: 1
                        }
                    }
                }
            }
        }
    })
);

const DataTable = (({ ...props }) => {
    const { data: attendanceData } = useAttendanceState();


    return (
        <StyledBox>
            <TableContainer >
                <TableHeaderContainer>
                    {Object.values(TABLE_DATA).map((th, i) => (
                        <TableCellContainer
                            sx={{ pl: 2, fontWeight: 'bold' }}
                            typography="caption" key={th}
                        >
                            {th}
                        </TableCellContainer>
                    ))}
                </TableHeaderContainer>
                <TableBody>
                    {attendanceData?.map((row, i) => (
                        <TableRow key={i}>
                            {Object.keys(TABLE_DATA).map((key, index) => {
                                switch (key) {
                                    case 'inTime':
                                        return (
                                            <TableCellContainer heading={key} key={key}>
                                                {row[key] ? formatTime(row[key]) : '-- --'}
                                            </TableCellContainer>
                                        )
                                    case 'outTime':
                                        return (
                                            <TableCellContainer heading={key} key={key}>
                                                {row[key] ? formatTime(row[key]) : '-- --'}
                                            </TableCellContainer>
                                        )
                                    case 'duration':
                                        return (
                                            <TableCellContainer heading={key} key={key}>
                                                {row[key] && convertToHoursAndMinuteFromSecond(row[key])}
                                            </TableCellContainer>
                                        )
                                    case 'status':
                                        return (
                                            <TableCellContainer heading={key} key={key}>
                                                {<StyledLabel
                                                    variant='body2'
                                                    sx={{ background: LEAVE_STATUS[row[key]] ? theme.palette[LEAVE_STATUS[row[key]].color].light : theme.palette.background.secondary, color: LEAVE_STATUS[row[key]] ? theme.palette[LEAVE_STATUS[row[key]].color].dark : theme.palette.text.main }}
                                                >{LEAVE_STATUS[row[key]]?.label || row[key]}</StyledLabel>}
                                            </TableCellContainer>
                                        )
                                    default:
                                        return (
                                            <TableCellContainer heading={key} key={key}>
                                                {row[key] ? row[key] : '-- --'}
                                            </TableCellContainer>
                                        )
                                }
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </TableContainer>
        </StyledBox>
    )
});

export default DataTable
